import React, {
    useState, useEffect, useCallback
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Grid, Text, Button, Flex, Image
} from 'components/ui';
import {
    colors, fontSizes, fontWeights, space, borders, radii, mediaQueries, shadows, lineHeights
} from 'style/config';
import biApi from 'services/api/beautyInsider';
import localeUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import UIUtils from 'utils/UI';
import MultiSegmentProgressBar from 'components/MultiSegmentProgressBar/MultiSegmentProgressBar.f';
import store from 'store/Store';
import Actions from 'Actions';
import { UserInfoReady } from 'constants/events';
import { HEADER_VALUE } from 'constants/authentication';
import LoyaltyProfileBannerEN from 'components/ProfileBanner/LoyaltyProfileBanner/locale/LoyaltyProfileBanner_en_US';
import LoyaltyProfileBannerFR from 'components/ProfileBanner/LoyaltyProfileBanner/locale/LoyaltyProfileBanner_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(LoyaltyProfileBannerEN, LoyaltyProfileBannerFR);

const BI_STATUS_TO_SEGMENT_INDEX = {
    BI: 0,
    VIB: 1,
    ROUGE: 2
};

const BI_SEGMENT_LABELS = ['Insider', 'VIB', 'Rouge'];

const LoyaltyProfileBanner = ({ user, isAtLeastRecognized, isBI, netBeautyBankPointsAvailable }) => {
    const [realTimeVIBMessages, setRealTimeVIBMessages] = useState([]);
    const [biPointsLoaded, setBiPointsLoaded] = useState(false);
    const [biPointsError, setBiPointsError] = useState(false);
    const [ytdSpending, setYtdSpending] = useState(null);
    const [vibThreshold, setVibThreshold] = useState(null);
    const [rougeThreshold, setRougeThreshold] = useState(null);
    const [biStatus, setBiStatus] = useState(null);
    const [statusDisplay, setStatusDisplay] = useState(null);

    const getBiPoints = useCallback(async () => {
        if (user?.profileId && isBI) {
            setBiPointsLoaded(false);
            setBiPointsError(false);
            try {
                const biInfo = await biApi.getBiPoints();
                setRealTimeVIBMessages(biInfo.realTimeVIBMessages);
                setYtdSpending(biInfo.vibSpendingForYear);
                setVibThreshold(biInfo.vibThreshold);
                setRougeThreshold(biInfo.rougeThreshold);
                setBiStatus(biInfo.biStatus);
                setStatusDisplay(userUtils.displayBiStatus(biInfo.biStatus));
                setBiPointsLoaded(true);
            } catch (error) {
                Sephora.logger.error('LoyaltyProfileBanner: failed to fetch BI points', error);
                setBiPointsError(true);
            }
        }
    }, [user?.profileId, isBI]);

    useEffect(() => {
        Sephora.Util.onLastLoadEvent(window, [UserInfoReady], () => {
            getBiPoints();
        });
    }, [user?.profileId, isBI]);

    const renderSignedIn = () => {
        const logoProps = {
            src: `/img/ufe/bi/new-logo-${statusDisplay?.toLowerCase()}.svg`,
            height: 22,
            alt: statusDisplay
        };

        // Check if we have all required data for progress bar
        const showProgressBar = biStatus && vibThreshold && rougeThreshold && ytdSpending !== null && ytdSpending !== undefined;

        return (
            <Grid css={styles.signedInContainer}>
                <Box css={styles.statusAndPointsGrid}>
                    <Box css={styles.statusAndPoints}>
                        <Box css={styles.statusItem}>
                            <Text
                                is='p'
                                children={getText('yourStatus')}
                                color={colors.gray}
                                css={styles.labelText}
                            />
                            <Box css={styles.valueContainer}>
                                <Image {...logoProps} />
                            </Box>
                        </Box>
                        <Box css={styles.pointsItem}>
                            <Text
                                is='p'
                                children={getText('pointsText')}
                                color={colors.gray}
                                css={styles.labelText}
                            />
                            <Box css={styles.valueContainer}>
                                <Text
                                    children={netBeautyBankPointsAvailable?.toLocaleString()}
                                    fontWeight={fontWeights.normal}
                                    fontSize={space[6]}
                                    lineHeight='tight'
                                    css={styles.bigNumber}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {showProgressBar && (
                    <Box css={styles.progressBarGrid}>
                        <MultiSegmentProgressBar
                            currentSegmentIndex={BI_STATUS_TO_SEGMENT_INDEX[biStatus] ?? 0}
                            currentValue={ytdSpending}
                            middleThreshold={vibThreshold}
                            maxThreshold={rougeThreshold}
                            segmentLabels={BI_SEGMENT_LABELS}
                            progressLabel={getText('tierProgressLabel')}
                        />
                    </Box>
                )}
                {realTimeVIBMessages?.length > 0 && (
                    <Box css={styles.messageGrid}>
                        {realTimeVIBMessages.map((item, i) => (
                            <Text
                                key={`vib_message_${i}`}
                                is='span'
                                lineHeight='tight'
                                marginBottom={4}
                                css={styles.tierMessageText}
                                dangerouslySetInnerHTML={{ __html: `${i >= 1 ? ' ' : ''}${item}` }}
                            />
                        ))}
                    </Box>
                )}
            </Grid>
        );
    };

    const renderSignedOut = () => {
        return (
            <Flex css={styles.flexContainer}>
                {/* Left: Branding */}
                <Box css={styles.brandingSection}>
                    <Text
                        is='p'
                        children={getText('joinFreeText')}
                        fontSize={fontSizes.base}
                        fontWeight={fontWeights.normal}
                        lineHeight={lineHeights.tight}
                    />
                    <Image
                        src='/img/ufe/bi/logo-beauty-insider-creative-refresh.svg'
                        height={28}
                        alt='Beauty Insider'
                    />
                </Box>
                <Box css={styles.verticalDivider} aria-hidden='true' />
                {/* Middle: Benefits */}
                <Box css={styles.benefitsSection}>
                    {[
                        getText('benefitShipping'),
                        getText('benefitBirthday'),
                        getText('benefitPoints')
                    ].map((text) => (
                        <Text
                            key={text}
                            is='p'
                            children={text}
                            fontSize={fontSizes.base}
                            lineHeight={lineHeights.tight}
                            fontWeight={fontWeights.normal}
                        />
                    ))}
                </Box>

                <Box css={styles.verticalDivider} aria-hidden='true' />

                {/* Right: CTAs */}
                {!isAtLeastRecognized ? (
                    <Box css={styles.ctaGroup} gap={[4, null, 5]}>
                        <Button
                            variant='primary'
                            width='100%'
                            onClick={() =>
                                store.dispatch(Actions.showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } }))}
                        >
                            {getText('signInBtn')}
                        </Button>
                        <Button
                            variant='secondary'
                            width='100%'
                            onClick={() => store.dispatch(Actions.showRegisterModal({ isOpen: true }))}
                        >
                            {getText('createAccountBtn')}
                        </Button>
                    </Box>
                ) : (
                    <Box css={styles.ctaGroupJoinNow}>
                        <Button
                            variant='primary'
                            width={['100%', null, '200px']}
                            onClick={() => store.dispatch(Actions.showRegisterModal({ isOpen: true }))}
                        >
                            {getText('joinNowBtn')}
                        </Button>
                    </Box>
                )}
            </Flex>
        );
    };
    const isLoading = isAtLeastRecognized && isBI && !biPointsLoaded && !biPointsError;

    return (
        <Grid css={[styles.container, isLoading && styles.containerLoading]}>
            {isLoading ? <Box css={styles.loadingState} /> : (
                isAtLeastRecognized && isBI ? (biPointsLoaded ? renderSignedIn() : null)
                    : renderSignedOut()
            )}
        </Grid>
    );
};

const styles = {
    container: {
        backgroundSize: `auto ${space[7]}px`,
        paddingBottom: space[5],
        [mediaQueries.smMax]: {
            position: 'relative',
            zIndex: 1,
            backgroundColor: colors.white,
            boxShadow: shadows.light,
            borderRadius: radii[2],
            padding: space[4]
        }
    },
    verticalDivider: {
        borderLeft: `${borders[1]} ${colors.lightGray}`,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        [mediaQueries.smMax]: {
            display: 'none'
        }
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: space[7],
        [mediaQueries.smMax]: {
            flexDirection: 'column',
            gap: space[4],
            justifyContent: 'flex-start',
            alignItems: 'stretch'
        }
    },
    brandingSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2],
        justifyContent: 'center',
        alignItems: 'flex-start',
        flexGrow: 0.5,
        [mediaQueries.smMax]: {
            marginBottom: -space[1]
        }
    },
    benefitsSection: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: space[1]
    },
    ctaGroup: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        alignItems: 'stretch',
        flexShrink: 0
    },
    ctaGroupJoinNow: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 0,
        flexShrink: 0,
        alignItems: 'stretch',
        justifyContent: 'flex-end',
        [mediaQueries.smMax]: {
            flexGrow: 1,
            justifyContent: 'center'
        }
    },
    loadingState: [
        UIUtils.SKELETON_ANIMATION,
        {
            borderRadius: space[1],
            minHeight: space[7],
            background: `linear-gradient(105deg, #FAFAFA 23.39%, ${colors.lightGray} 81.25%)`,
            [mediaQueries.smMax]: {
                width: '100%',
                minHeight: 'unset',
                aspectRatio: '343 / 142'
            }
        }
    ],
    signedInContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: space[5],
        alignItems: 'center',
        [mediaQueries.smMax]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: space[2]
        }
    },
    statusAndPointsGrid: {
        gridColumn: '1 / 4',
        [mediaQueries.smMax]: {
            gridColumn: 'auto'
        }
    },
    progressBarGrid: {
        gridColumn: '4 / 8',
        [mediaQueries.smMax]: {
            gridColumn: 'auto'
        }
    },
    messageGrid: {
        gridColumn: '9 / 13',
        [mediaQueries.smMax]: {
            gridColumn: 'auto'
        }
    },
    statusAndPoints: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        alignItems: 'flex-end',
        [mediaQueries.smMax]: {
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
        }
    },
    statusItem: {
        gridColumn: '1 / 3',
        [mediaQueries.smMax]: {
            gridColumn: 'auto'
        }
    },
    pointsItem: {
        gridColumn: '4 / 7',
        [mediaQueries.smMax]: {
            gridColumn: 'auto',
            textAlign: 'right'
        }
    },
    valueContainer: {
        height: 22,
        display: 'flex',
        alignItems: 'center',
        marginTop: 6
    },
    bigNumber: {
        wordWrap: 'normal',
        whiteSpace: 'nowrap'
    },
    labelText: {
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        fontWeight: fontWeights.normal
    },
    tierMessageText: {
        [mediaQueries.smMax]: {
            fontSize: fontSizes.sm,
            fontWeight: fontWeights.normal
        }
    },
    containerLoading: {
        [mediaQueries.smMax]: {
            padding: 0,
            paddingBottom: 0
        }
    }
};

export default wrapFunctionalComponent(LoyaltyProfileBanner, 'LoyaltyProfileBanner');
